import { useEffect, useMemo, useState } from 'react';
import type { AuthService } from './auth';
import { itemThumbUrl } from './itemArt';
import { Nav } from './Nav';
import { type Outfit, STACK_ORDER, type Slot, formatOutfit, parseOutfit, randomOutfit, removeSlot, toggleItem } from './outfit';
import { type CatalogEntry, catalogItems, mannequinUrl, wornLayer } from './wornArt';

const addressOutfit = (wearable: readonly CatalogEntry[]): Outfit => {
  const hash = window.location.hash;
  const q = hash.indexOf('?');
  return parseOutfit(new URLSearchParams(q < 0 ? '' : hash.slice(q + 1)), wearable);
};

const byName = (a: CatalogEntry, b: CatalogEntry) => a.name.localeCompare(b.name);

/** Try any mix of items on the mannequin, drawn the way the apps draw an avatar. Makes no request. */
export function MannequinPage({ auth }: { auth: AuthService }) {
  const wearable = useMemo(() => catalogItems.filter((i) => wornLayer(i.id)), []);
  const bySlot = useMemo(() => {
    const m = new Map<Slot, CatalogEntry[]>();
    for (const slot of STACK_ORDER) m.set(slot, catalogItems.filter((i) => i.slot === slot).sort(byName));
    return m;
  }, []);
  const byId = useMemo(() => new Map(catalogItems.map((i) => [i.id, i])), []);
  const [outfit, setOutfit] = useState<Outfit>(() => addressOutfit(wearable));
  const [filter, setFilter] = useState('');

  // Keep the outfit in the address. replaceState adds no history entry and fires no hashchange.
  useEffect(() => {
    const q = formatOutfit(outfit);
    const next = `#/mannequin${q ? `?${q}` : ''}`;
    if (window.location.hash !== next) window.history.replaceState(null, '', next);
  }, [outfit]);

  // An address pasted over this one (a shared link) replaces the outfit.
  useEffect(() => {
    const onHash = () => {
      const next = addressOutfit(wearable);
      setOutfit((cur) => (formatOutfit(cur) === formatOutfit(next) ? cur : next));
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [wearable]);

  const needle = filter.trim().toLowerCase();
  const matches = (i: CatalogEntry) => !needle || i.name.toLowerCase().includes(needle) || i.id.toLowerCase().includes(needle);

  return (
    <div className="shell">
      <header className="bar">
        <h1>EthrHunt Admin</h1>
        <Nav current="mannequin" />
        <button type="button" className="signout" onClick={() => void auth.signOut()}>Sign out</button>
      </header>
      <div className="mq">
        <section className="mq-left" aria-label="Outfit">
          <div className="mq-stage" data-testid="mannequin-stage">
            <img className="mq-layer mq-body" src={mannequinUrl} alt="" data-testid="mannequin-body" />
            {STACK_ORDER.map((slot) => {
              const id = outfit[slot];
              const layer = id ? wornLayer(id) : null;
              if (!layer) return null;
              const [x0, y0, x1, y1] = layer.box;
              return (
                <img
                  key={slot}
                  className="mq-layer mq-worn"
                  data-testid={`mannequin-slot-${slot}`}
                  src={layer.url}
                  alt=""
                  style={{ left: `${x0 * 100}%`, top: `${y0 * 100}%`, width: `${(x1 - x0) * 100}%`, height: `${(y1 - y0) * 100}%` }}
                />
              );
            })}
          </div>
          <div className="mq-controls">
            <button type="button" onClick={() => setOutfit(randomOutfit(wearable))}>Random</button>
            <button type="button" onClick={() => setOutfit({})}>Clear</button>
          </div>
          <ul className="mq-worn-list">
            {STACK_ORDER.map((slot) => {
              const item = outfit[slot] ? byId.get(outfit[slot]!) : undefined;
              return (
                <li key={slot}>
                  <span className="mq-slot">{slot}</span>
                  {item ? (
                    <>
                      <span data-testid={`worn-${slot}`}>{item.name}</span>
                      <button type="button" aria-label={`Remove ${slot}`} onClick={() => setOutfit(removeSlot(outfit, slot))}>Remove</button>
                    </>
                  ) : (
                    <span className="mq-none">nothing</span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
        <section className="mq-right" aria-label="Items">
          <label className="mq-filter">
            Filter
            <input type="search" placeholder="name or id" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </label>
          {STACK_ORDER.map((slot) => {
            const all = bySlot.get(slot)!;
            const shown = all.filter(matches);
            if (shown.length === 0) return null;
            return (
              <div key={slot}>
                <h2 className="mq-heading">{slot} ({all.length})</h2>
                <div className="mq-grid">
                  {shown.map((item) => {
                    const hasArt = wornLayer(item.id) !== null;
                    const thumb = itemThumbUrl(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className="mq-item"
                        aria-pressed={outfit[slot] === item.id}
                        disabled={!hasArt}
                        title={hasArt ? undefined : 'No worn art yet'}
                        data-testid={`item-${item.id}`}
                        onClick={() => setOutfit(toggleItem(outfit, item))}
                      >
                        {thumb ? <img src={thumb} alt="" loading="lazy" /> : <span className="mq-emoji" aria-hidden="true">{item.emoji}</span>}
                        <span className="mq-name">{item.name}</span>
                        <span className="mq-rarity">{item.rarity}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      </div>
    </div>
  );
}
